import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BRAND } from '@/brand.config';

export type SiteSettings = Record<string, string>;

// Defaults read from the compiled-in BRAND config; admins can override any
// of these by writing rows to the site_settings table.
const DEFAULTS: SiteSettings = {
  cta_button_text: BRAND.ctaButtonText,
  success_heading: BRAND.successHeading,
  success_subtext: BRAND.successSubtext,
  next_event_label: BRAND.nextEventLabel,
  signup_prompt: BRAND.signupPrompt,
  value_prop: BRAND.valueProp,
  hero_logo_url: '',
  from_email: '',
};

export function useSiteSettings() {
  return useQuery({
    queryKey: ['site-settings'],
    staleTime: 5 * 60 * 1000,
    retry: 2,
    queryFn: async (): Promise<SiteSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value');

      if (error) {
        console.error('[useSiteSettings] query error:', error);
        throw error;
      }

      const settings = { ...DEFAULTS };
      data?.forEach((row: { key: string; value: string }) => {
        settings[row.key] = row.value;
      });
      return settings;
    },
  });
}

export function useUpdateSiteSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Record<string, string>) => {
      const upserts = Object.entries(settings).map(([key, value]) => ({ key, value }));

      const { error } = await supabase
        .from('site_settings')
        .upsert(upserts, { onConflict: 'key' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
    },
  });
}

export function useUploadHeroLogo() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `hero-logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('tile-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('tile-images')
        .getPublicUrl(fileName);

      return data.publicUrl;
    },
  });
}
